import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type ConnectorPlatform,
  type ConnectorStatus,
  type MediaConnector,
  type SyncCadence,
  CAPABILITY_LABELS,
  MEDIA_CONNECTOR_QUERY_KEYS,
  connectWithServiceAccount,
  connectWithToken,
  disconnectConnector,
  testConnector,
  triggerSync,
  updateConnector,
} from '@/entities/media-connector';
import { notificationService } from '@/lib/services/notification.service';
import { track } from '@/shared/analytics';

interface UseMediaConnectorMutationsArgs {
  platform: ConnectorPlatform;
  /** Looked up by id in the disconnect mutation's onSuccess, for the analytics event. */
  accounts: MediaConnector[];
  onDeleted: () => void;
  onUpdated: () => void;
  onConnected: () => void;
}

/**
 * Every server write the detail page can trigger: test, sync, disconnect,
 * update, and the two connect flows (token / service account — OAuth goes
 * through `useOAuthPopup` instead). Kept apart from query/filter/dialog state
 * so neither half of the page hook grows past what fits in one read.
 */
export function useMediaConnectorMutations({
  platform,
  accounts,
  onDeleted,
  onUpdated,
  onConnected,
}: UseMediaConnectorMutationsArgs) {
  const { t } = useTranslation('knowledge');
  const queryClient = useQueryClient();

  /**
   * Reports what each capability answered, in the platform's own words where it failed.
   * The badges then carry the detail, so the toast stays a summary.
   */
  const testMutation = useMutation({
    mutationFn: testConnector,
    onSuccess: (connector) => {
      // Keys of ConnectorCapabilities, not copy.
      // eslint-disable-next-line i18next/no-literal-string
      const failing = (['publishing', 'reporting'] as const).filter(
        (capability) => connector.capabilities?.[capability].state === 'failing',
      );
      if (failing.length === 0) {
        notificationService.showSuccess(
          t('mediaConnectors.mediaConnectorDetail.everythingIsWorking'),
        );
      } else {
        notificationService.showError(
          t('mediaConnectors.mediaConnectorDetail.capabilitiesFailing', {
            count: failing.length,
            capabilities: failing
              .map((capability) => CAPABILITY_LABELS[capability])
              .join(t('mediaConnectors.mediaConnectorDetail.capabilityListJoiner')),
          }),
        );
      }
      void queryClient.invalidateQueries({ queryKey: MEDIA_CONNECTOR_QUERY_KEYS.all });
    },
    onError: () => {
      notificationService.showError(t('mediaConnectors.mediaConnectorDetail.couldNotTestAccount'));
    },
  });

  const triggerSyncMutation = useMutation({
    mutationFn: triggerSync,
    onSuccess: (_data, input) => {
      track('media_sync_triggered', {
        connector_id: input.connectorId,
        platform,
        trigger: 'manual',
      });
      notificationService.showSuccess(
        t('mediaConnectors.mediaConnectorDetail.syncStartedDataWillRefresh'),
      );
      void queryClient.invalidateQueries({
        queryKey: MEDIA_CONNECTOR_QUERY_KEYS.syncStatus(),
      });
    },
    onError: (error) => {
      if (!('response' in error) || !(error as { response?: unknown }).response) {
        notificationService.showError(t('mediaConnectors.mediaConnectorDetail.unableToStartSync'), {
          persist: true,
        });
      }
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectConnector,
    onSuccess: (_data, connectorId) => {
      const connector = accounts.find((c) => c.id === connectorId);
      if (connector) {
        track('media_connector_disconnected', { platform: connector.platform });
      }
      onDeleted();
      void queryClient.invalidateQueries({ queryKey: MEDIA_CONNECTOR_QUERY_KEYS.all });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      title?: string;
      tags?: string[];
      status?: ConnectorStatus;
      syncCadence?: SyncCadence;
      dsaBeneficiary?: string | null;
      dsaPayor?: string | null;
      advertiserId?: string | null;
      advertiserName?: string | null;
    }) => updateConnector(id, input),
    onSuccess: () => {
      onUpdated();
      void queryClient.invalidateQueries({ queryKey: MEDIA_CONNECTOR_QUERY_KEYS.all });
    },
  });

  const connectTokenMutation = useMutation({
    mutationFn: ({
      platform: p,
      title,
      tags,
      token,
      syncCadence,
      advertiserId,
    }: {
      platform: ConnectorPlatform;
      title: string;
      tags: string[];
      token: string;
      syncCadence?: SyncCadence;
      advertiserId?: string;
    }) => connectWithToken(p, title, tags, token, syncCadence, advertiserId),
    onSuccess: (connector) => {
      track('media_connector_connected', {
        platform,
        account_id: connector.externalAccountId ?? '',
      });
      onConnected();
      void queryClient.invalidateQueries({ queryKey: MEDIA_CONNECTOR_QUERY_KEYS.all });
    },
    onError: (error) => {
      track('media_connector_connect_failed', {
        platform,
        error_code: 'token_connect_error',
      });
      if (!('response' in error) || !(error as { response?: unknown }).response) {
        notificationService.showError(
          t('mediaConnectors.mediaConnectorDetail.unableToConnectCheckYour'),
          { persist: true },
        );
      }
    },
  });

  const connectServiceAccountMutation = useMutation({
    mutationFn: ({
      platform: p,
      title,
      tags,
      keyFileContent,
      syncCadence,
      loginCustomerId,
      advertiserId,
    }: {
      platform: ConnectorPlatform;
      title: string;
      tags: string[];
      keyFileContent: string;
      syncCadence?: SyncCadence;
      loginCustomerId?: string;
      advertiserId?: string;
    }) =>
      connectWithServiceAccount(
        p,
        title,
        tags,
        keyFileContent,
        syncCadence,
        loginCustomerId,
        advertiserId,
      ),
    onSuccess: (connector) => {
      track('media_connector_connected', {
        platform,
        account_id: connector.externalAccountId ?? '',
      });
      onConnected();
      void queryClient.invalidateQueries({ queryKey: MEDIA_CONNECTOR_QUERY_KEYS.all });
    },
    onError: (error) => {
      track('media_connector_connect_failed', {
        platform,
        error_code: 'service_account_connect_error',
      });
      if (!('response' in error) || !(error as { response?: unknown }).response) {
        notificationService.showError(
          t('mediaConnectors.mediaConnectorDetail.unableToConnectCheckYour'),
          { persist: true },
        );
      }
    },
  });

  return {
    testMutation,
    triggerSyncMutation,
    disconnectMutation,
    updateMutation,
    connectTokenMutation,
    connectServiceAccountMutation,
  };
}
